import React from "react";

/**
 * Mafia status timeline strip — Created → Edited → (current) → Confirmed → Released.
 *
 * Derives state from the preserved History chain (Round 9 keeps the full chain;
 * confirm appends "(part confirmed) <id>", release appends "(released) <id>").
 * This is a Mafia-scoped component (NOT part of the shared DashboardKit), so it
 * never affects other modules. Styling is injected once via a scoped <style>.
 *
 * Props:
 *   history       string  — the part's History value
 *   createdByLabel string — e.g. "Last revised by: 3325" or a name
 *   current       "editing" | "confirming" | "releasing" | null
 *   isImported    bool
 */
const MafiaStatusTimeline = ({ history, createdByLabel, current = null, isImported = false }) => {
  const raw = String(history || "");
  const h = raw.toLowerCase();
  const confirmed = h.includes("(part confirmed)") || h.includes("confirmed");
  const released = h.includes("(released)") || h.includes("released");
  const everEdited = h.includes("edit") || raw.includes(";");

  const createdSub = createdByLabel
    ? createdByLabel.replace("Last revised by: ", "").trim() || "—"
    : "—";

  const steps = [
    { key: "created", label: "Created", icon: "ri-add-circle-line", sub: createdSub, state: "done" },
    { key: "edited", label: "Edited", icon: "ri-edit-line", sub: everEdited ? "in history" : "—", state: everEdited ? "done" : "todo" },
  ];

  if (current === "editing") {
    steps.push({ key: "now", label: isImported ? "Importing" : "Editing now", icon: "ri-pencil-line", sub: "you · in progress", state: "active" });
    steps.push({ key: "confirm", label: "Confirmed", icon: "ri-checkbox-circle-line", sub: confirmed ? "done" : "pending", state: confirmed ? "done" : "todo" });
    steps.push({ key: "release", label: "Released", icon: "ri-rocket-line", sub: released ? "done" : "pending", state: released ? "done" : "todo" });
  } else if (current === "confirming") {
    steps.push({ key: "confirm", label: "Confirming now", icon: "ri-checkbox-circle-line", sub: "you · attesting", state: "active" });
    steps.push({ key: "release", label: "Released", icon: "ri-rocket-line", sub: released ? "done" : "pending", state: released ? "done" : "todo" });
  } else if (current === "releasing") {
    steps.push({ key: "confirm", label: "Confirmed", icon: "ri-checkbox-circle-line", sub: confirmed ? "done" : "pending", state: confirmed ? "done" : "todo" });
    steps.push({ key: "release", label: "Releasing now", icon: "ri-rocket-line", sub: "you · attesting", state: "active" });
  } else {
    steps.push({ key: "confirm", label: "Confirmed", icon: "ri-checkbox-circle-line", sub: confirmed ? "done" : "pending", state: confirmed ? "done" : "todo" });
    steps.push({ key: "release", label: "Released", icon: "ri-rocket-line", sub: released ? "done" : "pending", state: released ? "done" : "todo" });
  }

  return (
    <div className="mafia-timeline mb-3">
      <style>{TIMELINE_CSS}</style>
      {steps.map((s, i) => (
        <React.Fragment key={s.key}>
          {i > 0 && <div className={`mtl-line${steps[i - 1].state === "done" ? " done" : ""}`} />}
          <div className={`mtl-step mtl-${s.state}`}>
            <div className="mtl-dot"><i className={s.icon} /></div>
            <div>
              <div className="mtl-label">{s.label}</div>
              <div className="mtl-sub">{s.sub}</div>
            </div>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

const TIMELINE_CSS = `
.mafia-timeline{ display:flex; align-items:center; background: var(--ssw-surface, #fff); border-radius:14px;
  box-shadow:0 6px 18px rgba(33,37,41,.08); padding:14px 18px; overflow-x:auto; }
.mafia-timeline .mtl-step{ display:flex; align-items:center; gap:9px; white-space:nowrap; }
.mafia-timeline .mtl-dot{ width:28px; height:28px; border-radius:50%; display:flex; align-items:center;
  justify-content:center; font-size:14px; color:#fff; flex:none; }
.mafia-timeline .mtl-done .mtl-dot{ background:#0ab39c; }
.mafia-timeline .mtl-active .mtl-dot{ background:#6d5fda; box-shadow:0 0 0 4px #d7d2f7; }
.mafia-timeline .mtl-todo .mtl-dot{ background: var(--ssw-tint-gray, #cfd4dd); }
.mafia-timeline .mtl-label{ font-size:12.5px; font-weight:600; color: var(--ssw-fg-1, #1f2733); }
.mafia-timeline .mtl-sub{ font-size:11px; color: var(--ssw-fg-2, #7b8190); }
.mafia-timeline .mtl-line{ height:3px; width:42px; background: var(--ssw-surface-3, #e3e6ec); margin:0 12px; border-radius:2px; flex:none; }
.mafia-timeline .mtl-line.done{ background:#0ab39c; }
`;

export default MafiaStatusTimeline;
